import { Money } from "@hubrise/react-components"
import * as React from "react"
import { useTranslation } from "react-i18next"

import VariantPriceTooltip from "Catalog/Edit/shared/components/VariantPriceTooltip"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import Table from "shared/components/Table"
import useEditor from "shared/hooks/useEditor"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"
import { IOption, IOptionList } from "shared/hubrise/models"
import * as Option from "shared/hubrise/utils/Option"

import { FieldCheckbox, FieldName, FieldPrice, FieldRef } from "./Styles"

interface OptionsProps {
  optionList: IOptionList
}

const Options = ({ optionList }: OptionsProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigateNoUpdate()
  const { editor, catalog } = useEditor()
  const { currency } = catalog
  const { catalogNavigation } = useCatalogNavigation()

  const $newRowNameRef = React.useRef<HTMLInputElement>(null)

  const handleChange =
    <TField extends keyof IOption>(
      option: IOption,
      updateForNew: ((option: IOption) => void) | undefined,
      field: TField,
    ) =>
    (value: IOption[TField]) =>
      updateForNew
        ? updateForNew({ ...option, [field]: value })
        : editor.option.update(optionList.id, option.id, field, value)

  const handleReorder = (options: Array<IOption>): void => editor.optionList.reorderOptions(optionList.id, options)

  const handleDelete = (option: IOption): void => editor.option.delete(optionList.id, option.id)

  const canDelete = optionList.options.length >= 2

  const getNew = (): IOption => {
    setTimeout(() => $newRowNameRef.current!.focus(), 0)
    return Option.create(catalog)
  }

  const tryCreate = (option: IOption): boolean => {
    if (option.name.length > 0) {
      editor.option.add(optionList, option)
      return true
    } else {
      return false
    }
  }

  return (
    <Table
      items={optionList.options}
      columns={[
        {
          header: t("option_list.options.name"),
          cellRenderer: (option, updateForNew) => (
            <FieldName
              ref={updateForNew ? $newRowNameRef : undefined}
              initialValue={option.name}
              onChange={handleChange(option, updateForNew, "name")}
              debounce={updateForNew === undefined}
              validate={(name) => (name.length === 0 ? t("validations.not_empty") : undefined)}
              linethrough={!option.restrictions.enabled}
            />
          ),
        },
        {
          header: t("option_list.options.price"),
          cellRenderer: (option, updateForNew) => (
            <FieldPrice
              initialValue={option.price}
              onChange={(value) => handleChange(option, updateForNew, "price")(value || Money.zero(currency))}
              debounce={updateForNew === undefined}
              currency={currency}
            />
          ),
        },
        {
          header: t("option_list.options.ref"),
          cellRenderer: (option, updateForNew) => (
            <FieldRef
              initialValue={option.ref}
              onChange={handleChange(option, updateForNew, "ref")}
              debounce={updateForNew === undefined}
            />
          ),
        },
        {
          header: t("option_list.options.enabled"),
          style: "centered",
          cellRenderer: (option, updateForNew) => (
            <FieldCheckbox
              initialValue={option.restrictions.enabled}
              onChange={(enabled) => editor.option.enable(optionList.id, option.id, enabled)}
              debounce={updateForNew === undefined}
            />
          ),
        },
        {
          header: t("option_list.options.variants"),
          cellRenderer: (option) => (
            <VariantPriceTooltip
              catalog={catalog}
              navigateToGrid={() => navigate(catalogNavigation.gridPath("option", option.id))}
              priceOverrides={option.priceOverrides}
              restrictions={option.restrictions}
            />
          ),
        },
        {
          header: t("option_list.options.preselected"),
          style: "centered",
          cellRenderer: (option, updateForNew) => (
            <FieldCheckbox
              initialValue={option.isDefault}
              onChange={handleChange(option, updateForNew, "isDefault")}
              debounce={updateForNew === undefined}
            />
          ),
        },
      ]}
      onReorder={handleReorder}
      onDelete={canDelete ? handleDelete : undefined}
      droppableId={`options_${optionList.id}`}
      addBehavior={{ buttonLabel: t("option_list.options.add"), getNew, tryCreate }}
    />
  )
}

export default Options
